import { create } from 'zustand'

/**
 * Which container renders the companion while it is open.
 * - `drawer`: pinned to the right edge as the classic side drawer (default
 *   for a first open, per Chris 2026-09-30).
 * - `floating`: the draggable overlay window (top-right, above the mosaic,
 *   never interacting with it).
 *
 * Minimized is NOT a surface: that is `open === false`. The preference (and
 * the floating window's last position) persist across sessions in ONE small
 * localStorage key, matching the bounded-key rule.
 */
export type CompanionSurface = 'floating' | 'drawer'

export interface FloatingPosition {
  x: number
  y: number
}

const SURFACE_KEY = 'edge:companion:surface'

/**
 * The persisted preference. It also held the Ask/Build persona until Build
 * went away (2026-10-16); a stored `persona` key is ignored and dropped on the
 * next write.
 */
interface PersistedSurface {
  surface: CompanionSurface
  floatingPosition: FloatingPosition | null
}

function isValidSurface(value: unknown): value is CompanionSurface {
  return value === 'floating' || value === 'drawer'
}

function isValidPosition(value: unknown): value is FloatingPosition {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as FloatingPosition).x === 'number' &&
    typeof (value as FloatingPosition).y === 'number'
  )
}

function readPersistedSurface(): PersistedSurface {
  const fallback: PersistedSurface = {
    surface: 'drawer',
    floatingPosition: null,
  }
  try {
    const raw = localStorage.getItem(SURFACE_KEY)
    if (!raw) return fallback
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return fallback
    const candidate = parsed as Partial<PersistedSurface>
    return {
      surface: isValidSurface(candidate.surface) ? candidate.surface : 'drawer',
      floatingPosition: isValidPosition(candidate.floatingPosition)
        ? candidate.floatingPosition
        : null,
    }
  } catch {
    /* corrupt JSON / unavailable storage — fall back to defaults */
  }
  return fallback
}

function persistSurface(state: PersistedSurface): void {
  try {
    localStorage.setItem(SURFACE_KEY, JSON.stringify(state))
  } catch {
    /* ignore quota / unavailable storage */
  }
}

/**
 * Context seeded when a user asks the AI about a specific panel (via the panel's
 * insight-footer ✨ button). The chat drawer reads this to scope the next message
 * to that panel — its one-line insight plus the numbers behind it.
 */
export interface PanelInsightContext {
  panelId: string
  componentType: string
  /** The one-line insight shown in the footer — human- and AI-readable. */
  insight: string
  /** Structured numbers behind the insight, for the AI to reason over. */
  data?: Record<string, unknown>
}

/**
 * A panel referenced in the chat input — either via typing `@` and clicking a
 * panel, or via a panel's "Ask AI" kebab-menu item. Rendered as a removable
 * chip above the input; the drawer prepends a preamble naming these panels to
 * the outgoing message text (see chat-drawer.tsx `handleSend`).
 */
export interface PanelMention {
  panelId: string
  componentType: string
  /** Human-readable label, e.g. "Chart — AAPL" (see workspace/panel-title.ts). */
  title: string
}

interface ChatStoreState {
  /** Whether the chat drawer is open */
  open: boolean

  /** Container preference while open: floating overlay window or side drawer. */
  surface: CompanionSurface

  /** Last dragged position of the floating window; null = default top-right. */
  floatingPosition: FloatingPosition | null

  /** Panel the user is following up on, or null when the chat is page-scoped. */
  context: PanelInsightContext | null

  /** Text to pre-fill the chat input with on next open (e.g. "AAPL on
   *  2024-03-01: " from a chart's "Ask AI about this day"). The input consumes
   *  it once and clears it via setDraft(null) — it is NOT an auto-sent message,
   *  the user finishes the question and hits send. */
  draft: string | null

  /** Text to INSERT at the composer's cursor (a ticker dragged onto the
   *  chat). Unlike `draft` it never replaces what the user typed. The mounted
   *  composer consumes and clears it. */
  composerInsert: string | null

  /** The shortcut asked the mounted composer to put the cursor in its box.
   *  The composer consumes and clears it. */
  composerFocusPending: boolean

  /** Panels referenced in the input via `@`-mention or a panel's "Ask AI" menu item. */
  mentions: PanelMention[]

  // Actions
  toggle: () => void
  setOpen: (value: boolean) => void
  /** Switch container (pin to drawer / pop back to floating). Persisted. */
  setSurface: (surface: CompanionSurface) => void
  /** Commit the floating window's dragged position. Persisted. */
  setFloatingPosition: (position: FloatingPosition | null) => void
  /** Seed panel-scoped context (and, by default, open the drawer). */
  setContext: (context: PanelInsightContext, opts?: { open?: boolean }) => void
  clearContext: () => void
  /** Set (or clear) the pre-fill draft. The input clears it after seeding. */
  setDraft: (text: string | null) => void
  /** Open the drawer with the input pre-filled — the "ask about X" entry point. */
  openWithDraft: (text: string) => void
  /** Queue text for the open composer to insert at its cursor. */
  insertIntoComposer: (text: string) => void
  /** Called by the composer once it has inserted `composerInsert`. */
  clearComposerInsert: () => void
  /** Open the chat and ask the composer to take focus (the keyboard shortcut). */
  focusComposer: () => void
  /** Called by the composer once it has taken focus. */
  clearComposerFocus: () => void
  /** Add a panel mention, deduping by panelId (a repeat @-mention or "Ask AI" click is a no-op). */
  addMention: (mention: PanelMention) => void
  removeMention: (panelId: string) => void
  clearMentions: () => void
  /** Open the drawer with a panel already mentioned — the "Ask AI" menu entry point. */
  openWithMention: (mention: PanelMention) => void
}

const initialSurface = readPersistedSurface()

export const useChatStore = create<ChatStoreState>((set, get) => ({
  open: false,
  surface: initialSurface.surface,
  floatingPosition: initialSurface.floatingPosition,
  context: null,
  draft: null,
  composerInsert: null,
  composerFocusPending: false,
  mentions: [],

  toggle: () => set({ open: !get().open }),
  setOpen: (value) => set({ open: value }),
  setSurface: (surface) => {
    persistSurface({ surface, floatingPosition: get().floatingPosition })
    set({ surface })
  },
  setFloatingPosition: (floatingPosition) => {
    persistSurface({ surface: get().surface, floatingPosition })
    set({ floatingPosition })
  },
  setContext: (context, opts) => set({ context, open: opts?.open ?? true }),
  clearContext: () => set({ context: null }),
  setDraft: (draft) => set({ draft }),
  openWithDraft: (text) => set({ draft: text, open: true }),
  insertIntoComposer: (text) => set({ composerInsert: text }),
  clearComposerInsert: () => set({ composerInsert: null }),
  focusComposer: () => set({ open: true, composerFocusPending: true }),
  clearComposerFocus: () => set({ composerFocusPending: false }),
  addMention: (mention) =>
    set((state) =>
      state.mentions.some((m) => m.panelId === mention.panelId)
        ? state
        : { mentions: [...state.mentions, mention] },
    ),
  removeMention: (panelId) =>
    set((state) => ({ mentions: state.mentions.filter((m) => m.panelId !== panelId) })),
  clearMentions: () => set({ mentions: [] }),
  openWithMention: (mention) => {
    get().addMention(mention)
    set({ open: true })
  },
}))
